import { useTranslation } from 'react-i18next'
import type { LineStatus, OverallStatus } from '../types'

// PRICE_MISMATCH/VAT_MISMATCH/INVOICED_LT_DELIVERED zijn INFO-severity (backend:
// app.engine.reconciliation.FINDING_SEVERITY) — sky i.p.v. amber/yellow zodat ze minder zwaar
// ogen dan echte WARNING-bevindingen, conform XMLInsights-SDD
// requirements/processing-profiles.md REQ-PROCESSING-PROFILES-007/008/009. UOM_CONVERSION blijft
// blue (bestaande WARNING-kleur, ongewijzigd) — sky is bewust een ander tint om verwarring met
// UOM_CONVERSION te voorkomen.
const LINE_CONFIG: Record<LineStatus, { className: string }> = {
  OK:               { className: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' },
  PARTIAL_DELIVERY: { className: 'bg-yellow-100 text-yellow-800' },
  DELIVERED_LT_CONFIRMED: { className: 'bg-yellow-100 text-yellow-800' },
  INVOICED_LT_DELIVERED:  { className: 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300' },
  OVERBILLED:       { className: 'bg-yellow-100 text-yellow-800' },
  PRICE_MISMATCH:        { className: 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300' },
  PRICE_LIST_MISMATCH:   { className: 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300' },
  MISSING_INVOICE:  { className: 'bg-orange-100 text-orange-800' },
  UNKNOWN_ARTICLE:  { className: 'bg-purple-100 text-purple-800' },
  MISMATCH:          { className: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' },
  NO_ARTICLE_NUMBER: { className: 'bg-orange-100 text-orange-800' },
  SINGLE_DOCUMENT:   { className: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400' },
  INCOMPLETE_COMPARISON: { className: 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400' },
  UOM_CONVERSION:    { className: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' },
  VAT_MISMATCH:      { className: 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300' },
}

const OVERALL_CONFIG: Record<OverallStatus, { className: string }> = {
  OK:      { className: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-200' },
  INFO:    { className: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 border-blue-200 dark:border-blue-800/50' },
  WARNING: { className: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  ERROR:   { className: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 border-red-200 dark:border-red-800/50' },
  UNKNOWN: { className: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200 dark:border-gray-700' },
}

const LINE_LABEL_KEYS: Record<LineStatus, string> = {
  OK:                'status_badge.line.ok',
  PARTIAL_DELIVERY:  'status_badge.line.partial_delivery',
  DELIVERED_LT_CONFIRMED: 'status_badge.line.delivered_lt_confirmed',
  INVOICED_LT_DELIVERED:  'status_badge.line.invoiced_lt_delivered',
  OVERBILLED:        'status_badge.line.overbilled',
  PRICE_MISMATCH:    'status_badge.line.price_mismatch',
  PRICE_LIST_MISMATCH: 'status_badge.line.price_list_mismatch',
  MISSING_INVOICE:   'status_badge.line.missing_invoice',
  UNKNOWN_ARTICLE:   'status_badge.line.unknown_article',
  MISMATCH:          'status_badge.line.mismatch',
  NO_ARTICLE_NUMBER: 'status_badge.line.no_article_number',
  SINGLE_DOCUMENT:   'status_badge.line.single_document',
  INCOMPLETE_COMPARISON: 'status_badge.line.incomplete_comparison',
  UOM_CONVERSION:    'status_badge.line.uom_conversion',
  VAT_MISMATCH:      'status_badge.line.vat_mismatch',
}

const OVERALL_LABEL_KEYS: Record<OverallStatus, string> = {
  OK:      'status_badge.overall.ok',
  INFO:    'status_badge.overall.info',
  WARNING: 'status_badge.overall.warning',
  ERROR:   'status_badge.overall.error',
  UNKNOWN: 'status_badge.overall.unknown',
}

export function LineStatusBadge({ status }: { status: LineStatus }) {
  const { t } = useTranslation()
  const cfg = LINE_CONFIG[status] ?? LINE_CONFIG.MISMATCH
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
      {t(LINE_LABEL_KEYS[status] ?? 'status_badge.line.mismatch')}
    </span>
  )
}

export function OverallStatusBadge({ status }: { status: OverallStatus }) {
  const { t } = useTranslation()
  const cfg = OVERALL_CONFIG[status] ?? OVERALL_CONFIG.UNKNOWN
  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${cfg.className}`}>
      {t(OVERALL_LABEL_KEYS[status] ?? 'status_badge.overall.unknown')}
    </span>
  )
}
